import { NgModule, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslocoModule } from '@ngneat/transloco';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule } from '@angular/material/stepper';
import { ModalComponent } from './components/modal/modal.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import { UploadComponent } from './components/upload/upload.component';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NoContentCardComponent } from './components/no-content-card/no-content-card.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { RouterModule } from '@angular/router';
import { LogoComponent } from './components/logo/logo.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatToolbarModule } from '@angular/material/toolbar';
import { LomyLogoComponent } from './components/lomy-logo/lomy-logo.component';
import { LomyPointsComponent } from './components/lomy-points/lomy-points.component';
import { PointsRatioFieldComponent } from './components/points-ratio-field/points-ratio-field.component';
import { ImageUploadFieldComponent } from './components/image-upload-field/image-upload-field.component';
import { PhonePreviewComponent } from './components/phone-preview/phone-preview.component';
import { DailyLimitStepperComponent } from './components/daily-limit-stepper/daily-limit-stepper.component';
import { FormFooterComponent } from './components/form-footer/form-footer.component';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { ButtonComponent } from './components/button/button.component';
import { UploadPreviewComponent } from './components/upload-preview/upload-preview.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HighlightDirective } from './directives/highlight.directive';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { TimePickerComponent } from './components/time-picker/time-picker.component';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { ToggleComponent } from './components/toggle/toggle.component';
import { NgxEditorModule, schema } from 'ngx-editor';
import { ModalCustomComponent } from './components/modal-custom/modal-custom.component';
import { MatSliderModule } from '@angular/material/slider';
import { GoogleMapsModule } from '@angular/google-maps';
import { BackofficeInfoComponent } from './components/backoffice-info/backoffice-info.component';
import { MatExpansionModule } from '@angular/material/expansion';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1;
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
    return ['Ned', 'Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub'];
  }
}

const textEditorProps = {
  locals: {
    // menu
    bold: 'Bold',
    italic: 'Italic',
    code: 'Code',
    underline: 'Underline',
    strike: 'Strike',
    blockquote: 'Blockquote',
    bullet_list: 'Bullet List',
    ordered_list: 'Ordered List',
    heading: 'Heading',
    h1: 'Header 1',
    h2: 'Header 2',
    h3: 'Header 3',
    h4: 'Header 4',
    h5: 'Header 5',
    h6: 'Header 6',
    align_left: 'Left Align',
    align_center: 'Center Align',
    align_right: 'Right Align',
    align_justify: 'Justify',
    text_color: 'Text Color',
    background_color: 'Background Color',
    horizontal_rule: 'Horizontal rule',
    format_clear: 'Clear Formatting',
    insertLink: 'Insert Link',
    removeLink: 'Remove Link',
    insertImage: 'Insert Image',
    indent: 'Increase Indent',
    outdent: 'Decrease Indent',
    superscript: 'Superscript',
    subscript: 'Subscript',
    undo: 'Undo',
    redo: 'Redo',
  },
};
@NgModule({
  declarations: [
    LomyPointsComponent,
    PointsRatioFieldComponent,
    ImageUploadFieldComponent,
    PhonePreviewComponent,
    DailyLimitStepperComponent,
    FormFooterComponent,
    EmptyStateComponent,
    ModalComponent,
    UploadComponent,
    SpinnerComponent,
    NoContentCardComponent,
    PageHeaderComponent,
    LogoComponent,
    LomyLogoComponent,
    ButtonComponent,
    UploadPreviewComponent,
    HighlightDirective,
    TimePickerComponent,
    ToggleComponent,
    ModalCustomComponent,
    BackofficeInfoComponent,
  ],
  imports: [
    CommonModule,
    MatSnackBarModule,
    TranslocoModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatStepperModule,
    MatDialogModule,
    MatRadioModule,
    MatTableModule,
    TextFieldModule,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    RouterModule,
    MatCheckboxModule,
    FormsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatDividerModule,
    MatSelectModule,
    MatTabsModule,
    NgxMatTimepickerModule,
    MatSliderModule,
    NgxEditorModule.forRoot(textEditorProps),
    GoogleMapsModule,
    MatExpansionModule,
  ],
  exports: [
    LomyPointsComponent,
    PointsRatioFieldComponent,
    ImageUploadFieldComponent,
    PhonePreviewComponent,
    DailyLimitStepperComponent,
    FormFooterComponent,
    EmptyStateComponent,
    MatSnackBarModule,
    TranslocoModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatStepperModule,
    ModalComponent,
    MatDialogModule,
    MatRadioModule,
    MatTableModule,
    CdkTextareaAutosize,
    TextFieldModule,
    UploadComponent,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    SpinnerComponent,
    NoContentCardComponent,
    PageHeaderComponent,
    RouterModule,
    LogoComponent,
    MatCheckboxModule,
    FormsModule,
    MatToolbarModule,
    LomyLogoComponent,
    ButtonComponent,
    UploadPreviewComponent,
    MatTooltipModule,
    HighlightDirective,
    MatDividerModule,
    MatSelectModule,
    MatTabsModule,
    NgxMatTimepickerModule,
    TimePickerComponent,
    ToggleComponent,
    NgxEditorModule,
    ModalCustomComponent,
    MatSliderModule,
    MatExpansionModule,
    BackofficeInfoComponent,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        color: 'accent',
        floatLabel: 'always',
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
    },
    CdkTextareaAutosize,
  ],
})
export class SharedModule {}
